import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateNotificationId } from '../../utils/idGenerator';

export type ThemeMode = 'light' | 'dark';
export type Language = 'en' | 'es' | 'fr' | 'de'; // Add more languages as needed

interface NotificationState {
  id: string;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
  autoHideDuration?: number;
}

interface UIState {
  // Theme and appearance
  themeMode: ThemeMode;
  sidebarOpen: boolean;
  
  // Internationalization
  language: Language;
  
  // Loading states
  isLoading: boolean;
  loadingMessage?: string;
  
  // Notifications/Snackbars
  notifications: NotificationState[];
  
  // Mobile responsiveness
  isMobile: boolean;
  
  // Page state
  pageTitle: string;
  breadcrumbs: Array<{ label: string; path?: string }>;
}

const initialState: UIState = {
  themeMode: 'light',
  sidebarOpen: false,
  language: 'en',
  isLoading: false,
  notifications: [],
  isMobile: false,
  pageTitle: '',
  breadcrumbs: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleTheme: (state) => {
      state.themeMode = state.themeMode === 'light' ? 'dark' : 'light';
    },
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.themeMode = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setLanguage: (state, action: PayloadAction<Language>) => {
      state.language = action.payload;
    },
    setLoading: (state, action: PayloadAction<{ isLoading: boolean; message?: string }>) => {
      state.isLoading = action.payload.isLoading;
      state.loadingMessage = action.payload.message ?? '';
    },
    addNotification: (state, action: PayloadAction<Omit<NotificationState, 'id'>>) => {
      const notification: NotificationState = {
        ...action.payload,
        id: generateNotificationId(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
    },
    setIsMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload;
    },
    setPageTitle: (state, action: PayloadAction<string>) => {
      state.pageTitle = action.payload;
    },
    setBreadcrumbs: (state, action: PayloadAction<Array<{ label: string; path?: string }>>) => {
      state.breadcrumbs = action.payload;
    },
  },
});

export const {
  toggleTheme,
  setThemeMode,
  toggleSidebar,
  setSidebarOpen,
  setLanguage,
  setLoading,
  addNotification,
  removeNotification,
  clearAllNotifications,
  setIsMobile,
  setPageTitle,
  setBreadcrumbs,
} = uiSlice.actions;

export default uiSlice.reducer;